// Implements: F-CNV-015, F-UIX-009
/**
 * Continuing from a version, as one action that never rejects.
 *
 * It answers `null` when the pointer moved (or already stood there) and the refusal itself
 * when it did not, and it answers AS SOON AS the server has: the refreshes that follow are
 * started, not awaited, so the notice never waits for four reads. They run only when
 * something may have changed: after a success, and after a `409` (a turn or a run holds the
 * mockup, so the page should stop offering what it cannot do). Any other refusal changed
 * nothing and refreshes nothing.
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { continueFromVersion } from "../../../shared/api/continueFromVersion";
import { invalidateMockupPointer } from "../../../shared/api/invalidateMockupPointer";

export function useContinueFromVersion(): (
  mockupId: string,
  versionId: string,
) => Promise<ApiError | null> {
  const queryClient = useQueryClient();

  return useCallback(
    async (mockupId: string, versionId: string): Promise<ApiError | null> => {
      try {
        await continueFromVersion(mockupId, versionId);
      } catch (cause) {
        const refusal = asApiError(cause);
        if (refusal.status === 409) void invalidateMockupPointer(queryClient, mockupId);
        return refusal;
      }
      void invalidateMockupPointer(queryClient, mockupId);
      return null;
    },
    [queryClient],
  );
}
